/**
 * Pure helpers for the MatchupMatrixModal — extracted into a non-JSX file
 * so unit tests can import them without dragging the React + Emotion
 * rendering pipeline through Jest's babel transform.
 *
 * The modal itself imports from here; tests import from here. Keep this
 * file free of React imports so the surface stays trivially testable.
 */

import type { SchedulerConstraint } from '../hooks/useSchedulerConstraints'
import type { TimeSlot } from '../hooks/useSchedulerTimeSlots'

/**
 * Per-pair count override map. Key: '<uid_a>|<uid_b>' canonical-sorted.
 * Value: integer game count for that pair.
 */
export type MatchupMatrix = Record<string, number>

/**
 * Build a canonical pair key. Sorted lexicographically so the key for
 * (A, B) is identical to (B, A) — required for symmetry and matches the
 * scheduler-python validator's expectation.
 */
export const pairKey = (uidA: string, uidB: string): string =>
  [uidA, uidB].sort().join('|')

/**
 * Build the matrix-shape view of the existing pair_matchup_count constraints
 * for this division (canonical pair key → count). Skips malformed rows
 * (wrong type code, wrong team_uids count, non-numeric count) so callers
 * see a clean dict.
 */
export function constraintsToMatrix(
  constraints: SchedulerConstraint[]
): MatchupMatrix {
  const matrix: MatchupMatrix = {}
  for (const c of constraints) {
    if (c.type.code !== 'pair_matchup_count') {
      continue
    }
    const teamUids = c.team_uids ?? []
    if (teamUids.length !== 2) {
      continue
    }
    const count = c.parameters.count
    if (typeof count !== 'number') {
      continue
    }
    matrix[pairKey(teamUids[0], teamUids[1])] = count
  }
  return matrix
}

/**
 * Build a lookup from canonical pair key to existing constraint id, so we
 * can route each grid cell change to add/update/delete.
 */
export function constraintsToIdMap(
  constraints: SchedulerConstraint[]
): Record<string, string> {
  const map: Record<string, string> = {}
  for (const c of constraints) {
    if (c.type.code !== 'pair_matchup_count') {
      continue
    }
    const teamUids = c.team_uids ?? []
    if (teamUids.length !== 2) {
      continue
    }
    map[pairKey(teamUids[0], teamUids[1])] = c.id
  }
  return map
}

/**
 * Set of canonical pair keys whose existing constraint is "global" —
 * i.e. has division_uids === null (or empty). Used by the matrix modal
 * to seed the per-cell "Apply to all divisions" toggle and to detect
 * scope changes during the save diff.
 */
export function constraintsToGlobalKeys(
  constraints: SchedulerConstraint[]
): Set<string> {
  const out = new Set<string>()
  for (const c of constraints) {
    if (c.type.code !== 'pair_matchup_count') {
      continue
    }
    const teamUids = c.team_uids ?? []
    if (teamUids.length !== 2) {
      continue
    }
    if (c.division_uids === null || c.division_uids.length === 0) {
      out.add(pairKey(teamUids[0], teamUids[1]))
    }
  }
  return out
}

/**
 * Save operation kinds emitted by computeSaveOperations. The handler maps
 * each to the corresponding constraints API call (addConstraint /
 * updateConstraint / deleteConstraint).
 *
 * `divisionUids` semantics:
 *   - `null` → constraint applies to all divisions (a "global" override)
 *   - `[divisionUid]` → constraint applies only to that one division
 */
export type MatchupSaveOperation =
  | {
      kind: 'add'
      teamUids: [string, string]
      count: number
      divisionUids: string[] | null
    }
  | {
      kind: 'update'
      constraintId: string
      count: number
      /** Only set when the user toggled the cell's scope (per-division ↔ global). */
      divisionUids?: string[] | null
    }
  | { kind: 'delete'; constraintId: string }

export interface SaveDiffInputs {
  /** Desired count per pair key. Pairs not present here will be deleted. */
  desiredMatrix: MatchupMatrix
  /** Subset of `desiredMatrix` keys the user marked as global. */
  desiredGlobalKeys: Set<string>
  /** All pair_matchup_count constraints currently visible in this matrix view. */
  existingConstraints: SchedulerConstraint[]
  /** Division currently being edited — used as the scope for non-global cells. */
  currentDivisionUid: string
}

/**
 * Pure diff function: given the desired override matrix and the existing
 * pair_matchup_count constraints, return the list of ops needed to
 * reconcile.
 *
 *   - Pair in matrix but no existing constraint → add
 *   - Pair in matrix with existing constraint, count differs → update
 *   - Pair in matrix with existing constraint, scope differs → update with new divisionUids
 *   - Pair in matrix with existing constraint, same count + scope → no-op
 *   - Existing constraint, pair NOT in matrix → delete (reset to default)
 *
 * No side effects, no API calls — caller applies the ops via
 * addConstraint/updateConstraint/deleteConstraint.
 */
export function computeSaveOperations(
  inputs: SaveDiffInputs
): MatchupSaveOperation[] {
  const {
    desiredMatrix,
    desiredGlobalKeys,
    existingConstraints,
    currentDivisionUid,
  } = inputs

  const ops: MatchupSaveOperation[] = []
  const existingIdByKey = constraintsToIdMap(existingConstraints)
  const existingCountByKey = constraintsToMatrix(existingConstraints)
  const existingGlobalKeys = constraintsToGlobalKeys(existingConstraints)

  // Adds and updates
  for (const [key, count] of Object.entries(desiredMatrix)) {
    const [uidA, uidB] = key.split('|') as [string, string]
    const wantsGlobal = desiredGlobalKeys.has(key)
    const desiredScope: string[] | null = wantsGlobal
      ? null
      : [currentDivisionUid]
    const existingId = existingIdByKey[key]
    if (existingId === undefined) {
      ops.push({
        kind: 'add',
        teamUids: [uidA, uidB],
        count,
        divisionUids: desiredScope,
      })
      continue
    }
    const countChanged = existingCountByKey[key] !== count
    const scopeChanged = existingGlobalKeys.has(key) !== wantsGlobal
    if (countChanged || scopeChanged) {
      const op: MatchupSaveOperation = {
        kind: 'update',
        constraintId: existingId,
        count,
      }
      if (scopeChanged) {
        op.divisionUids = desiredScope
      }
      ops.push(op)
    }
  }

  // Deletes (existing pair no longer in desired matrix)
  for (const [key, existingId] of Object.entries(existingIdByKey)) {
    if (!(key in desiredMatrix)) {
      ops.push({ kind: 'delete', constraintId: existingId })
    }
  }

  return ops
}

/**
 * Per-division plan emitted by the modal's save flow — the ops list
 * for one division plus the desired override count (used for toast
 * copy and feasibility KPIs).
 */
export interface MatchupSavePlan {
  divisionId: string
  ops: MatchupSaveOperation[]
  desiredCount: number
}

/**
 * Bulk-save payload shape consumed by `useSchedulerConstraints.bulkSaveConstraints`.
 * Pulled out as a pure helper so the per-team-mode and division-total-mode
 * inputs can be round-trip checked without mounting the modal.
 */
export interface MatchupBulkSavePayload {
  add: {
    constraint_type_code: 'pair_matchup_count'
    parameters: { count: number }
    team_uids: [string, string]
    division_uids: string[] | null
    is_hard: true
    is_enabled: true
  }[]
  update: {
    id: string
    data: {
      parameters: { count: number }
      division_uids?: string[] | null
    }
  }[]
  delete: string[]
}

/**
 * Collapse a list of per-division plans into the `{add, update, delete}`
 * shape the bulk endpoint expects. Mirrors what the modal used to do
 * inline before the bulk endpoint existed.
 */
export function buildBulkSavePayload(
  plans: MatchupSavePlan[]
): MatchupBulkSavePayload {
  const add: MatchupBulkSavePayload['add'] = []
  const update: MatchupBulkSavePayload['update'] = []
  const del: string[] = []
  for (const plan of plans) {
    for (const op of plan.ops) {
      if (op.kind === 'add') {
        add.push({
          constraint_type_code: 'pair_matchup_count',
          parameters: { count: op.count },
          team_uids: [op.teamUids[0], op.teamUids[1]],
          division_uids: op.divisionUids,
          is_hard: true,
          is_enabled: true,
        })
      } else if (op.kind === 'update') {
        const data: {
          parameters: { count: number }
          division_uids?: string[] | null
        } = {
          parameters: { count: op.count },
        }
        if (op.divisionUids !== undefined) {
          data.division_uids = op.divisionUids
        }
        update.push({ id: op.constraintId, data })
      } else {
        del.push(op.constraintId)
      }
    }
  }
  return { add, update, delete: del }
}

/**
 * Compute per-team game counts from a matrix + uniform default.
 * Each team's total is the sum across all pairs it appears in.
 *
 * Used by the modal's per-team panel + imbalance warning.
 */
export function computePerTeamCounts(
  teams: { id: string }[],
  matrix: MatchupMatrix,
  gamesPerMatchup: number
): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const t of teams) {
    counts[t.id] = 0
  }
  for (let i = 0; i < teams.length; i += 1) {
    for (let j = i + 1; j < teams.length; j += 1) {
      const key = pairKey(teams[i].id, teams[j].id)
      const c = matrix[key] ?? gamesPerMatchup
      counts[teams[i].id] += c
      counts[teams[j].id] += c
    }
  }
  return counts
}

/**
 * Sum the per-pair matchup counts for a division. Pairs not present in the
 * matrix fall back to the uniform `gamesPerMatchup`. Mirrors the same
 * formula scheduler-python uses to size the schedule.
 */
export function computeTotalGames(
  teams: { id: string }[],
  matrix: MatchupMatrix,
  gamesPerMatchup: number
): number {
  if (teams.length < 2) {
    return 0
  }
  let total = 0
  for (let i = 0; i < teams.length; i += 1) {
    for (let j = i + 1; j < teams.length; j += 1) {
      const key = pairKey(teams[i].id, teams[j].id)
      total += matrix[key] ?? gamesPerMatchup
    }
  }
  return total
}

/**
 * Result of computing an auto-balanced override layout for a per-team
 * games target. Built so the modal can apply it (or warn cleanly) without
 * any graph theory leaking into the JSX.
 */
export interface AutoBalanceResult {
  /** Base games_per_matchup that all unboosted pairs will play. */
  baseGamesPerMatchup: number
  /**
   * Pairs (canonical sorted pairKey) that should receive +1 game on top
   * of the base. Length is `(N * remainder) / 2` when achievable.
   */
  boostedPairKeys: string[]
  /**
   * True when the target distributes evenly — every team ends up with
   * exactly `target` games. False when the parity makes a perfect
   * solution impossible (N*remainder is odd).
   */
  achievable: boolean
  /**
   * The closest uniform per-team value we can reach without overrides.
   * Used by the modal to show "closest are X or Y" when achievable=false.
   */
  closestUniformBelow: number
  closestUniformAbove: number
}

/**
 * Generate the per-pair boosts needed so every team plays exactly
 * `targetGamesPerTeam` games in a round-robin format with `teamIds.length`
 * teams. Returns null when the target is below the floor of 0 or the
 * input is degenerate.
 *
 * Strategy:
 *   1. base = floor(target / (N - 1)). Every pair plays this many games.
 *   2. remainder = target - base * (N - 1). Each team needs this many
 *      ADDITIONAL games, distributed across `(N * remainder) / 2` boosted
 *      pairs (each boost adds +1 to two teams).
 *   3. Build an `r`-regular graph on the team set using a circulant
 *      construction: connect team i to teams i+1..i+r/2 (and i+N/2 when
 *      r is odd, only valid when N is even).
 *   4. When N*r is odd (N odd AND r odd), no perfect solution exists —
 *      return achievable=false with the closest uniform values so the
 *      caller can communicate the constraint.
 *
 * The circulant construction is well-known to produce r-regular graphs
 * for any (N, r) where N*r is even and r < N.
 */
export function buildAutoBalanceOverrides(
  teamIds: string[],
  targetGamesPerTeam: number
): AutoBalanceResult | null {
  const n = teamIds.length
  if (n < 2 || targetGamesPerTeam < 0) {
    return null
  }
  const opponentsPerTeam = n - 1
  const base = Math.max(0, Math.floor(targetGamesPerTeam / opponentsPerTeam))
  const remainder = targetGamesPerTeam - base * opponentsPerTeam
  const closestUniformBelow = base * opponentsPerTeam
  const closestUniformAbove = (base + 1) * opponentsPerTeam

  if (remainder === 0) {
    return {
      baseGamesPerMatchup: base,
      boostedPairKeys: [],
      achievable: true,
      closestUniformBelow,
      closestUniformAbove,
    }
  }

  // Parity gate: N * r must be even for a perfect r-regular distribution.
  if ((n * remainder) % 2 !== 0) {
    return {
      baseGamesPerMatchup: base,
      boostedPairKeys: [],
      achievable: false,
      closestUniformBelow,
      closestUniformAbove,
    }
  }

  // Circulant construction. Indices into teamIds are the graph nodes.
  const edgeSet = new Set<string>()
  const half = Math.floor(remainder / 2)
  for (let i = 0; i < n; i += 1) {
    for (let k = 1; k <= half; k += 1) {
      const j = (i + k) % n
      const a = Math.min(i, j)
      const b = Math.max(i, j)
      edgeSet.add(`${a}|${b}`)
    }
  }
  // When `remainder` is odd, add the antipodal edges (only well-defined
  // when N is even — and N*remainder being even with odd remainder
  // implies N is even).
  if (remainder % 2 === 1) {
    const halfN = n / 2
    for (let i = 0; i < halfN; i += 1) {
      const j = i + halfN
      const a = Math.min(i, j)
      const b = Math.max(i, j)
      edgeSet.add(`${a}|${b}`)
    }
  }

  const boostedPairKeys: string[] = []
  for (const edgeKey of edgeSet) {
    const [aIdx, bIdx] = edgeKey.split('|').map(Number)
    boostedPairKeys.push(pairKey(teamIds[aIdx], teamIds[bIdx]))
  }

  return {
    baseGamesPerMatchup: base,
    boostedPairKeys,
    achievable: true,
    closestUniformBelow,
    closestUniformAbove,
  }
}

// ---------------------------------------------------------------------------
// Feasibility estimation
//
// The Matchup Config modal asks one question — "how many games per team?" —
// and tells the user whether the answer is achievable given the available
// time slots. This estimate is conservative: raw concrete-slot count vs.
// required matchups, no facility/per-day refinement. The scheduler-python
// partition pass remains authoritative; this client-side check is just so
// the user gets feedback before they hit Save / Generate.
// ---------------------------------------------------------------------------

/**
 * Count concrete slot-instances within `[startDate, endDate]` (inclusive)
 * for the given active time-slot templates.
 *
 * Mirrors scheduler-python's `expand_time_slot_templates` at a coarse
 * grain: each template emits one game-slot per (date, time-range) within
 * the range. Recurrence semantics:
 *   - 'specific'   → 1 instance if `slot_date` falls in range; else 0
 *   - 'weekly'     → for each `days[]` entry, count matching weekdays in
 *                    the range × `time_ranges.length`
 *   - 'biweekly'   → like weekly but every other week (start at first
 *                    matching weekday)
 *   - 'monthly'    → for each `days[]` entry, count one match per month in
 *                    range × `time_ranges.length`
 *
 * Returns 0 for an empty / invalid range.
 */
export function expandSlotsCount(
  timeSlots: TimeSlot[],
  startDate: string,
  endDate: string
): number {
  if (!startDate || !endDate) {
    return 0
  }
  const start = new Date(`${startDate}T00:00:00`)
  const end = new Date(`${endDate}T00:00:00`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 0
  }
  if (end.getTime() < start.getTime()) {
    return 0
  }
  const oneDayMs = 86_400_000
  const totalDays = Math.floor((end.getTime() - start.getTime()) / oneDayMs) + 1

  let total = 0
  for (const slot of timeSlots) {
    if (!slot.is_active) {
      continue
    }
    const trCount = slot.time_ranges?.length ?? 0
    if (trCount === 0) {
      continue
    }
    if (slot.recurrence_type === 'specific') {
      const date = slot.slot_date
      if (!date) {
        continue
      }
      if (date >= startDate && date <= endDate) {
        total += trCount
      }
      continue
    }
    const days = slot.days ?? []
    if (days.length === 0) {
      continue
    }
    if (slot.recurrence_type === 'weekly') {
      // Count matching weekdays in the range.
      for (const dow of days) {
        total += countWeekdaysInRange(start, totalDays, dow) * trCount
      }
      continue
    }
    if (slot.recurrence_type === 'biweekly') {
      for (const dow of days) {
        total +=
          Math.ceil(countWeekdaysInRange(start, totalDays, dow) / 2) * trCount
      }
      continue
    }
    if (slot.recurrence_type === 'monthly') {
      // One match per month per `days[]` entry, ignoring nth-of-month
      // semantics (we don't have that detail in the template). Coarse.
      const monthsInRange = countMonthsInRange(start, end)
      total += days.length * monthsInRange * trCount
    }
  }
  return total
}

/**
 * Count the number of times `dow` (0=Sun..6=Sat) occurs in the
 * `totalDays`-long window starting at `start`.
 */
function countWeekdaysInRange(
  start: Date,
  totalDays: number,
  dow: number
): number {
  if (totalDays <= 0) {
    return 0
  }
  const startDow = start.getDay()
  // Days from start to first matching weekday (0..6).
  const offset = (dow - startDow + 7) % 7
  if (offset >= totalDays) {
    return 0
  }
  return Math.floor((totalDays - offset - 1) / 7) + 1
}

/** Inclusive month-count between two dates. */
function countMonthsInRange(start: Date, end: Date): number {
  const months =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth())
  return Math.max(0, months) + 1
}

export interface FeasibilityInput {
  /** Number of teams in the division being checked. */
  divisionTeamCount: number
  /** User's target games-per-team for this division. */
  gamesPerTeam: number
  /**
   * Total concrete slot-instances available across all facilities for the
   * flight's date range. From `expandSlotsCount()`.
   */
  totalAvailableSlots: number
  /**
   * Slots already accounted for by *other* divisions in this flight. Pass
   * the running sum so each division's check accounts for the others'
   * draws on the shared slot pool.
   */
  alreadyAllocatedSlots?: number
  /**
   * Team UIDs in the division — passed through to `buildAutoBalanceOverrides`
   * for the auto-balance achievability check.
   */
  teamUids: string[]
}

export type FeasibilityResult =
  | {
      kind: 'feasible'
      gamesNeeded: number
      slotsRemaining: number
      autoBalanced: boolean
    }
  | {
      kind: 'auto-balance-impossible'
      gamesNeeded: number
      closestBelow: number
      closestAbove: number
    }
  | {
      kind: 'infeasible'
      maxAchievable: number
      gamesNeeded: number
      slotsAvailable: number
    }

/**
 * Compute feasibility verdict for one division's target games-per-team.
 *
 * Three states:
 *   - **feasible**: target achieves a valid schedule; auto-balance may
 *     have been applied internally if the math wasn't a clean integer
 *     across opponents (`autoBalanced` flag tells the caller).
 *   - **auto-balance-impossible**: the target can't be balanced cleanly
 *     even with per-pair boosts (parity gate fails); UI should suggest
 *     the closest values that work.
 *   - **infeasible**: target exceeds slot capacity; UI should suggest
 *     the max achievable target.
 */
export function checkFeasibility(input: FeasibilityInput): FeasibilityResult {
  const {
    divisionTeamCount: n,
    gamesPerTeam,
    totalAvailableSlots,
    alreadyAllocatedSlots = 0,
    teamUids,
  } = input

  const opponents = Math.max(0, n - 1)
  // Each game involves 2 teams → games-per-team × N / 2 = total games.
  const gamesNeeded = opponents === 0 ? 0 : Math.ceil((n * gamesPerTeam) / 2)
  const slotsAvailableForThisDiv = Math.max(
    0,
    totalAvailableSlots - alreadyAllocatedSlots
  )

  // Hard capacity check first — slot scarcity beats balance considerations.
  if (gamesNeeded > slotsAvailableForThisDiv) {
    const maxGames = slotsAvailableForThisDiv
    const maxAchievable = n > 0 ? Math.floor((maxGames * 2) / n) : 0
    return {
      kind: 'infeasible',
      maxAchievable,
      gamesNeeded,
      slotsAvailable: slotsAvailableForThisDiv,
    }
  }

  // Trivial cases: 0 or 1 team, or zero target — nothing to balance.
  if (opponents === 0 || gamesPerTeam === 0) {
    return {
      kind: 'feasible',
      gamesNeeded,
      slotsRemaining: slotsAvailableForThisDiv - gamesNeeded,
      autoBalanced: false,
    }
  }

  // Clean division — every pair plays the same count.
  if (gamesPerTeam % opponents === 0) {
    return {
      kind: 'feasible',
      gamesNeeded,
      slotsRemaining: slotsAvailableForThisDiv - gamesNeeded,
      autoBalanced: false,
    }
  }

  // Doesn't divide evenly — try to auto-balance via boosted pairs.
  const proposal = buildAutoBalanceOverrides(teamUids, gamesPerTeam)
  if (proposal === null) {
    // Defensive: shouldn't happen in this branch (we already returned for
    // n<2 / 0-target above), but treat as feasible-without-balance.
    return {
      kind: 'feasible',
      gamesNeeded,
      slotsRemaining: slotsAvailableForThisDiv - gamesNeeded,
      autoBalanced: false,
    }
  }
  if (proposal.achievable) {
    return {
      kind: 'feasible',
      gamesNeeded,
      slotsRemaining: slotsAvailableForThisDiv - gamesNeeded,
      autoBalanced: true,
    }
  }
  return {
    kind: 'auto-balance-impossible',
    gamesNeeded,
    closestBelow: proposal.closestUniformBelow,
    closestAbove: proposal.closestUniformAbove,
  }
}

// ============================================================================
// Backend feasibility wire format
// ============================================================================

/**
 * One row in the request body posted to
 * `POST /scheduler/flights/{uid}/feasibility-check`.
 *
 * The contract is the unsaved modal proposal: explicit per-team target,
 * the base uniform games-per-matchup, and the unsaved per-pair overrides.
 * api2 turns this into in-memory `pair_matchup_count` constraints and
 * scheduler-python uses the same matrix-aware helpers as partition + solver
 * to compute the verdict — so a chip "feasible" reflects what generation
 * would actually produce, not a rough client estimate.
 */
export interface FeasibilityRequestDivision {
  uid: string
  games_per_team_target: number
  base_games_per_matchup: number
  pair_matchup_counts: { team_uids: [string, string]; count: number }[]
}

/**
 * The shape of one verdict row returned in `data.divisions[*]` from the
 * feasibility-check endpoint. Backed by the Pydantic
 * `DivisionFeasibility` model in scheduler-python.
 */
export interface BackendFeasibilityVerdict {
  division_uid: string
  feasible: boolean
  games_per_team_target: number
  games_per_team_max: number
  games_needed: number
  slots_estimated_for_division: number
  reason: 'capacity' | 'ok' | 'balance_impossible'
}

/**
 * Build the wire body for the feasibility-check endpoint from the modal's
 * per-division entries. `entry.matrix` keys are canonical pipe-delimited
 * pairs (`a|b`, sorted) — split them back into the contract shape.
 */
export function buildFeasibilityDivisionsBody(
  entries: {
    div: { id: string }
    entry: { matrix: MatchupMatrix; gamesPerMatchup: number }
    perTeam: number
  }[]
): FeasibilityRequestDivision[] {
  return entries.map(({ div, entry, perTeam }) => ({
    uid: div.id,
    games_per_team_target: perTeam,
    base_games_per_matchup: entry.gamesPerMatchup,
    pair_matchup_counts: Object.entries(entry.matrix).map(([key, count]) => {
      const [a, b] = key.split('|')
      return { team_uids: [a, b] as [string, string], count }
    }),
  }))
}

/**
 * Map a backend verdict to the modal's `FeasibilityResult` chip state.
 *
 * `balance_impossible` is the new server reason — it covers proposals
 * whose pair counts can't realize the requested per-team target (parity
 * fails). For now we derive `closestBelow` / `closestAbove` locally from
 * `buildAutoBalanceOverrides`; surfacing those from the server is a
 * follow-up.
 */
export function mapBackendVerdictToFeasibility(
  verdict: BackendFeasibilityVerdict,
  teamUids: string[]
): FeasibilityResult {
  if (verdict.feasible) {
    return {
      kind: 'feasible',
      gamesNeeded: verdict.games_needed,
      slotsRemaining:
        verdict.slots_estimated_for_division - verdict.games_needed,
      autoBalanced: false,
    }
  }
  if (verdict.reason === 'balance_impossible') {
    const proposal = buildAutoBalanceOverrides(
      teamUids,
      verdict.games_per_team_target
    )
    return {
      kind: 'auto-balance-impossible',
      gamesNeeded: verdict.games_needed,
      closestBelow: proposal?.closestUniformBelow ?? 0,
      closestAbove: proposal?.closestUniformAbove ?? 0,
    }
  }
  return {
    kind: 'infeasible',
    maxAchievable: verdict.games_per_team_max,
    gamesNeeded: verdict.games_needed,
    slotsAvailable: verdict.slots_estimated_for_division,
  }
}

// ============================================================================
// Per-team ↔ division-total conversion (Matchup Config input mode toggle)
// ============================================================================
//
// "Games per team" is the canonical product contract. The modal also lets
// admins enter a division total instead, mathematically linked by
//   total = teamCount * gamesPerTeam / 2
// (each game contributes one game to two teams). The toggle is local UI
// state — only the resolved per-team value reaches the backend.
//
// Parity: when teamCount is odd, gamesPerTeam must be even for total to
// land on an integer. When teamCount is even, every integer gamesPerTeam
// produces an integer total.

/**
 * Division total games derived from a per-team target.
 *
 * Returns 0 when teamCount < 2 (no pairs ⇒ no games). Assumes
 * `gamesPerTeam` is non-negative; callers clamp at the input.
 */
export function divisionTotalFromGamesPerTeam(
  teamCount: number,
  gamesPerTeam: number
): number {
  if (teamCount < 2 || gamesPerTeam < 0) {
    return 0
  }
  // Each pair contributes one game to two teams; total = n * perTeam / 2.
  // Caller is expected to pass valid (per-team * teamCount) where the
  // product is even — when the proposal is canonical this always holds.
  return Math.floor((teamCount * gamesPerTeam) / 2)
}

/**
 * Inverse of `divisionTotalFromGamesPerTeam`.
 *
 * Returns null when the requested total cannot split evenly across teams
 * (i.e. `total * 2` is not divisible by `teamCount`, or inputs are
 * degenerate). Callers route a null result to the "Invalid total" chip
 * and use `closestBalancedDivisionTotals` to surface achievable
 * neighbours.
 */
export function gamesPerTeamFromDivisionTotal(
  teamCount: number,
  totalGames: number
): number | null {
  if (teamCount < 2 || totalGames < 0) {
    return null
  }
  const numerator = totalGames * 2
  if (numerator % teamCount !== 0) {
    return null
  }
  return numerator / teamCount
}

/**
 * Closest valid division totals for an invalid input.
 *
 * Snaps the implied per-team value to the nearest valid integers
 * below/above (parity-aware for odd team counts) and back-computes their
 * totals. Returns null when the input is already valid (no suggestion
 * needed) or when inputs are degenerate.
 *
 * Examples:
 *   - 6 teams, 50 → { below: { total: 48, perTeam: 16 },
 *                     above: { total: 51, perTeam: 17 } }
 *   - 5 teams, 11 → { below: { total: 10, perTeam: 4 },
 *                     above: { total: 15, perTeam: 6 } }   (parity-aware)
 */
export function closestBalancedDivisionTotals(
  teamCount: number,
  totalGames: number
): {
  below: { total: number; perTeam: number }
  above: { total: number; perTeam: number }
} | null {
  if (teamCount < 2 || totalGames < 0) {
    return null
  }
  if (gamesPerTeamFromDivisionTotal(teamCount, totalGames) !== null) {
    return null
  }

  // Smallest integer step on perTeam that preserves parity. For odd team
  // counts perTeam must be even, so the step is 2; for even team counts
  // any integer perTeam is valid, step 1.
  const step = teamCount % 2 === 1 ? 2 : 1

  const perTeamRaw = (totalGames * 2) / teamCount

  // Snap perTeamRaw to the nearest multiple of `step` below and above.
  let perTeamBelow = Math.floor(perTeamRaw / step) * step
  if (perTeamBelow < 0) {
    perTeamBelow = 0
  }
  let perTeamAbove = perTeamBelow + step
  // If perTeamRaw happens to land exactly on a valid value (shouldn't
  // happen because gamesPerTeamFromDivisionTotal already returned null,
  // but guard anyway) bump above by one step.
  if (perTeamAbove === perTeamRaw) {
    perTeamAbove += step
  }

  return {
    below: {
      perTeam: perTeamBelow,
      total: divisionTotalFromGamesPerTeam(teamCount, perTeamBelow),
    },
    above: {
      perTeam: perTeamAbove,
      total: divisionTotalFromGamesPerTeam(teamCount, perTeamAbove),
    },
  }
}
